import { createRouter, createWebHistory } from 'vue-router'

// Constants
const SUBSCRIPTION_URL = 'https://dsplp.sd.zain.com/af-lp/?p=2887461945'
const REQUIRED_AUTH_COOKIES = ['msisdn', 'status']

/**
 * Lazy load views from the views directory
 */
const lazyLoad = (view) => () => import(`@/views/${view}.vue`)

/**
 * Lazy load pages from the views/Page directory
 */
const lazyPage = (view) => () => import(`@/views/Page/${view}.vue`)

/**
 * Lazy load user pages from the views/User directory
 */
const lazyUser = (view) => () => import(`@/views/User/${view}.vue`)

/**
 * Route definitions
 */
const routes = [
  // Public routes
  {
    path: '/',
    name: 'Home',
    component: lazyLoad('HomeView'),
  },
  {
    path: '/game',
    name: 'Game',
    component: () => import(/* webpackChunkName: "game" */ '../views/Page/GameView.vue'),
  },
  {
    path: '/islamyat',
    name: 'Islamyat',
    component: lazyPage('IslamyatView'),
  },
  {
    path: '/kitchen',
    name: 'Kitchen',
    component: lazyPage('KitchenView'),
  },
  {
    path: '/kids',
    name: 'Kids',
    component: lazyPage('KidsView'),
  },
  {
    path: '/search/:search_id',
    name: 'Search',
    component: lazyPage('SearchView'),
    props: true,
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "auth" */ '../views/User/LoginView.vue'),
  },
  {
    path: '/subscribe',
    name: 'Subscribe',
    component: () => import(/* webpackChunkName: "auth" */ '../views/User/SubscribeView.vue'),
  },
  {
    path: '/thank-you',
    name: 'ThankYou',
    component: () => import(/* webpackChunkName: "auth" */ '../views/User/ThankYouView.vue'),
  },

  // Protected routes (require authentication)
  {
    path: '/sound/:id',
    name: 'Sound',
    component: lazyPage('SoundView'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/content/:id',
    name: 'Content',
    component: lazyPage('ContentView'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/gameDetail/:id',
    name: 'GameDetail',
    component: lazyPage('GameDetailView'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/play/:id',
    name: 'Play',
    component: lazyPage('PlayView'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/contact',
    name: 'Contact',
    component: lazyPage('ContactView'),
    meta: { requiresAuth: true },
  },
  {
    path: '/unsubscribe',
    name: 'Unsubscribe',
    component: lazyUser('UnsubscribeView'),
    meta: { requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: lazyUser('ProfileView'),
    meta: { requiresAuth: true },
  },

  // 404 - must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "notFound" */ '../views/Page/NotFoundView.vue'),
  },
]

/**
 * Create router instance
 */
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  scrollBehavior(to, from, savedPosition) {
    // Return saved scroll position if available, otherwise scroll to top
    return savedPosition || { top: 0 }
  },
})

/**
 * Check if user has required authentication cookies
 */
const isUserAuthenticated = ($cookie) => {
  return REQUIRED_AUTH_COOKIES.every(cookieName =>
    $cookie.isCookieAvailable(cookieName)
  )
}

/**
 * Handle authentication check before navigation
 */
router.beforeEach((to, from, next) => {
  const { $cookie } = router.app.config.globalProperties

  // Returning from the Zain landing page: tbuzz.club/??msisdn=249xxxxxxxxx
  // The extra "?" makes the parsed query key "?msisdn" instead of "msisdn"
  if (to.name === 'Home') {
    const msisdn = to.query.msisdn || to.query['?msisdn']
    if (msisdn) {
      next({ path: '/thank-you', query: { msisdn: String(msisdn) } })
      return
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (isUserAuthenticated($cookie)) {
      // User is authenticated, allow navigation
      next()
    } else {
      // User is not authenticated, redirect to subscription
      window.open(SUBSCRIPTION_URL, '_self')
      next(false)
    }
  } else {
    // Route doesn't require auth, allow navigation
    next()
  }
})

export default router
